/**
 * Entry point.
 *
 * The roster is fetched before the app mounts. It is 22 KB gzipped and cached
 * after first open, and every screen past Home needs it, so there is nothing
 * useful to render without it — a skeleton here would just be a slower way to
 * show the same thing.
 */

import './styles/fonts.css';
import './styles/tokens.css';
import './styles/app.css';
import { mount } from 'svelte';
import App from './App.svelte';
import { loadRoster } from './lib/roster';
import { initAnalytics, trackErrors, trackLoad } from './lib/analytics';
import { registerServiceWorker } from './lib/sw';

const target = document.getElementById('app')!;

/**
 * Wrapped rather than top-level await: TLA forces the build target up to
 * browsers that support it, and the baseline here is deliberately wide — this
 * is a phone game in India first.
 */
async function start(): Promise<void> {
  // Before the fetch, so a first open that fails to load still reports itself.
  initAnalytics();
  trackErrors();
  registerServiceWorker();

  try {
    const before = performance.now();
    const roster = await loadRoster();
    // A warm cache returns in single-digit milliseconds; a cold fetch of 22 KB
    // gzipped does not. The threshold only has to separate those two worlds.
    trackLoad(performance.now() - before < 50);
    mount(App, { target, props: { roster } });
  } catch (error) {
    // Failing visibly beats a blank olive rectangle. This is also the one
    // error path the user can act on: it is nearly always offline-on-first-open.
    console.error('roster failed to load', error);
    target.innerHTML = `
      <div class="app"><div class="body" style="display:grid;place-items:center;text-align:center">
        <div>
          <h2>Could not load the squads</h2>
          <p class="muted">Check your connection and try again.</p>
          <button class="btn-primary" id="retry">Retry</button>
        </div>
      </div></div>`;
    document.getElementById('retry')?.addEventListener('click', () => location.reload());
  }
}

void start();
